import { useState } from 'react';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';
import ToastStack from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';

/**
 * Refunding a booking.
 *
 * The terms promise a refund when an owner withdraws, or when the
 * accommodation does not exist or plainly does not match the offer. Neither
 * can be judged by code, so both are a human decision — and until now the
 * only way to act on one was the Stripe dashboard, which moved the money
 * while Staynow's own records still said the booking completed.
 *
 * Deliberately keyed by booking id rather than a browsable list: there is no
 * bookings-list endpoint, and inventing a "pick a booking to refund" browser
 * would invite refunding by scrolling. A refund should start from a specific
 * case — a support thread, a Stripe payment — not from a list.
 */
export default function Refunds() {
  const { toasts, push, dismiss } = useToast();
  const [bookingId, setBookingId] = useState('');
  const [reason, setReason] = useState('');
  const [partial, setPartial] = useState(false);
  const [amount, setAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const trimmedId = bookingId.trim();
  const trimmedReason = reason.trim();
  const amountCents = partial
    ? Math.round(Number(amount.replace(',', '.')) * 100)
    : undefined;

  const amountInvalid =
    partial && (!Number.isFinite(amountCents) || (amountCents ?? 0) < 1);
  const canSubmit =
    trimmedId.length > 0 && trimmedReason.length >= 3 && !amountInvalid;

  async function submit() {
    setConfirming(false);
    setBusy(true);
    try {
      const result = await api.refundBooking(
        trimmedId,
        trimmedReason,
        amountCents,
      );
      push(
        `Remboursement de ${(result.data.refunded_amount / 100).toFixed(2)} $ effectué.`,
        'success',
      );
      setBookingId('');
      setReason('');
      setAmount('');
      setPartial(false);
    } catch (err) {
      push(err instanceof Error ? err.message : 'Une erreur est survenue.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ToastStack toasts={toasts} dismiss={dismiss} />

      <div className="section-head">
        <div>
          <div className="section-title">Rembourser une réservation</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            À utiliser quand le séjour n’a pas pu avoir lieu du fait du
            bénéficiaire de l’offre.
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 20, maxWidth: 620 }}>
        <p style={{ fontSize: 13, lineHeight: 1.6, marginTop: 0 }}>
          Le remboursement reprend d’abord le versement déjà fait au
          bénéficiaire, puis rend l’argent au voyageur. Les deux mouvements
          sont liés&nbsp;: si la reprise échoue, rien n’est remboursé, pour ne
          pas payer le bénéficiaire à même le solde de Staynow.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          Cette action est <strong>irréversible</strong> et une réservation ne
          peut être remboursée qu’une seule fois.
        </p>

        <label htmlFor="refund-booking-id" style={{ marginTop: 18 }}>
          Identifiant de la réservation
        </label>
        <input
          id="refund-booking-id"
          type="text"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          autoComplete="off"
          spellCheck={false}
        />

        <label htmlFor="refund-reason">
          Motif
        </label>
        <input
          id="refund-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex. : logement inaccessible, signalé par le voyageur"
          autoComplete="off"
        />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Le motif est conservé dans le journal. C’est la seule trace de la
          décision.
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 16,
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={partial}
            onChange={(e) => setPartial(e.target.checked)}
          />
          Remboursement partiel
        </label>

        {partial && (
          <>
            <label htmlFor="refund-amount">
              Montant à rembourser ($)
            </label>
            <input
              id="refund-amount"
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="42.50"
              inputMode="decimal"
              autoComplete="off"
            />
            {amountInvalid && (
              <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                Entrez un montant valide.
              </div>
            )}
          </>
        )}

        <button
          type="button"
          className="btn btn-danger"
          style={{ marginTop: 20 }}
          disabled={!canSubmit || busy}
          onClick={() => setConfirming(true)}
        >
          {busy ? 'Remboursement en cours…' : 'Rembourser'}
        </button>
      </div>

      {confirming && (
        <ConfirmModal
          title="Confirmer le remboursement"
          description={
            partial
              ? `Rembourser ${amount} $ sur la réservation ${trimmedId} ? Le versement au bénéficiaire sera repris à proportion.`
              : `Rembourser la totalité de la réservation ${trimmedId} ? Le versement déjà fait au bénéficiaire sera repris.`
          }
          confirmLabel="Rembourser"
          confirmVariant="danger"
          onConfirm={submit}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
